import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./asset/accountInfoHolder.module.scss";
import { useDispatch, useSelector } from "react-redux";
import * as userActions from "../../reduxStore/actions/userActionsHelper.js";
import { usersData$ } from "../../reduxStore/selectors/userSelectors.js";
import { useOptionalAnnouncements } from "../AnnouncementModal/context/AnnouncementContext";
import * as userApi from '../../api/index';
import { clearAccessToken } from '../../api/config';
import DemoExitModal from '../demoExitModal/DemoExitModal';
import { hasRole, USER_ROLES } from '../../utils/Enum/RoleEnum';

const IS_DEMO = process.env.REACT_APP_ENVCONFIG === 'demo';

const AccountInfoHolder = () => {
  const userData = useSelector(usersData$) || {};
  const { username, job, avatar } = userData;
  const [showDropdown, setShowDropdown] = useState(false);
  const [showEndDemoModal, setShowEndDemoModal] = useState(false);
  const [isEndingDemo, setIsEndingDemo] = useState(false);
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { openAnnouncements, hasUnreadAnnouncements } = useOptionalAnnouncements();

  // Check if current page is user-settings
  const isUserSettingsPage = location.pathname === '/user-setting';
  const isAdminPortalPage = location.pathname.startsWith('/admin-portal');
  const isAdminUser = hasRole(userData, [USER_ROLES.ADMIN]);

  // Use user's avatar URL or fall back to Prepal logo
  const avatarSrc = avatar || require("../../publicAsset/media/Prepal Logo 3.svg").default;

  const handleLogout = () => {
    if (IS_DEMO) {
      setShowEndDemoModal(true);
      setShowDropdown(false);
      return;
    }

    dispatch(userActions.logoutUser.request());
    setShowDropdown(false);
  };

  const handleConfirmEndDemo = async () => {
    setIsEndingDemo(true);
    try {
      await userApi.logout();
    } catch (err) {
      // Ignore logout failures here — the goal is to exit the demo cleanly on the client.
    } finally {
      clearAccessToken();
      dispatch(userActions.logoutUser.success());
      window.location.assign('https://studyprepal.com');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <div className={styles.userDashboard}>
      <DemoExitModal
        open={showEndDemoModal}
        variant="manualEnd"
        onPrimary={handleConfirmEndDemo}
        onSecondary={() => setShowEndDemoModal(false)}
        isPrimaryLoading={isEndingDemo}
      />
      <div className={styles.frameParent}>
        {!isUserSettingsPage && (
          <div className={styles.usersParent}>
            <div className={styles.users}>
              <div className={styles.colorBg} />
              <img className={styles.philipMartin5aguycwPjwUnspIcon} alt="User Avatar" src={avatarSrc} />
            </div>
            <div className={styles.helloHoaTruongParent}>
              <div className={styles.helloHoaTruong}>Hello, {username || 'User'}</div>
              <div className={styles.student}>{job || 'Undefined'}</div>
            </div>
          </div>
        )}
        <div className={styles.usersParent}>
          <div
            className={styles.bellContainer}
            onClick={openAnnouncements}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            <img
              className={styles.communicationBellNotificat}
              alt="Notifications"
              src={require("../../publicAsset/media/Bell_Notification.svg").default}
            />
            {hasUnreadAnnouncements && (
              <span className={styles.notificationDot} />
            )}
          </div>
          <div style={{ position: 'relative' }} ref={dropdownRef}>
            <button
              className={styles.communicationBellNotificat}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              onClick={() => setShowDropdown((prev) => !prev)}
              aria-label="Menu"
            >
              <img alt="Menu" src={require("../../publicAsset/media/Menu_Alt.svg").default} />
            </button>
            {showDropdown && (
              <div className={styles.dropdownMenu}>
                {isAdminUser && !isAdminPortalPage && (
                  <button
                    className={styles.secondaryMenuButton}
                    onClick={() => {
                      navigate('/admin-portal');
                      setShowDropdown(false);
                    }}
                  >
                    <svg className={styles.secondaryIcon} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2l7 4v6c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6l7-4zm0 4.2L7 9v3c0 3.5 2.2 6 5 7.5 2.8-1.5 5-4 5-7.5V9l-5-2.8z" />
                    </svg>
                    Admin Portal
                  </button>
                )}

                {isAdminPortalPage && (
                  <button
                    className={styles.secondaryMenuButton}
                    onClick={() => {
                      navigate('/user-dashboard');
                      setShowDropdown(false);
                    }}
                  >
                    <svg className={styles.secondaryIcon} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14 7l-5 5 5 5v-3h7v-4h-7V7zM3 5h8v2H5v10h6v2H3V5z" />
                    </svg>
                    Back to Dashboard
                  </button>
                )}

                <button
                  className={IS_DEMO ? styles.endDemoButton : styles.logoutButton}
                  onClick={handleLogout}
                >
                  {IS_DEMO ? (
                    <svg className={styles.logoutIcon} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z" />
                    </svg>
                  ) : (
                    <svg className={styles.logoutIcon} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 17v-3H9v-4h7V7l5 5-5 5M14 2a2 2 0 012 2v2h-2V4H4v16h10v-2h2v2a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2h10z" />
                    </svg>
                  )}
                  {IS_DEMO ? 'End Demo' : 'Logout'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountInfoHolder;
